import { useQuery } from '@tanstack/react-query'
import { authClient } from '../lib/auth-client.ts'
import type { User } from '../types.ts'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'

type MeResponse = {
  user: {
    id: string
    email: string
    name: string | null
    emailVerified?: boolean
    image?: string | null
  }
  domainUser: User & {
    auth_user_id: string
    enabled: boolean
    admin: boolean
  }
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'
type DomainUserStatus = 'idle' | 'loading' | 'ready' | 'error'

async function fetchCurrentUser(): Promise<MeResponse> {
  //console.log('[useCurrentUser] fetching /me')

  const response = await fetch(`${API_BASE_URL}/me`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error('Erro ao buscar usuário atual')
  }

  const data = await response.json() as MeResponse
  console.log('[useCurrentUser] /me payload', data)

  return data
}

export function useCurrentUser() {
  const { data: session, isPending: isSessionPending } = authClient.useSession()
  const authUser = session?.user ?? null
  const authStatus: AuthStatus = isSessionPending
    ? 'loading'
    : authUser
      ? 'authenticated'
      : 'unauthenticated'

  const meQuery = useQuery({
    queryKey: ['current-user', authUser?.id],
    queryFn: fetchCurrentUser,
    enabled: authStatus === 'authenticated',
    staleTime: 60_000,
  })

  const domainUser = authStatus === 'authenticated' ? meQuery.data?.domainUser ?? null : null
  let domainUserStatus: DomainUserStatus = 'idle'

  if (authStatus === 'loading') {
    domainUserStatus = 'loading'
  } else if (authStatus === 'authenticated') {
    if (meQuery.isError) {
      domainUserStatus = 'error'
    } else if (meQuery.isPending) {
      domainUserStatus = 'loading'
    } else {
      domainUserStatus = meQuery.data ? 'ready' : 'idle'
    }
  }

  return {
    session,
    authUser,
    authStatus,
    domainUser,
    domainUserStatus,
    userId: domainUser?.id ?? 0,
    isAdmin: domainUser?.admin ?? false,
    isEnabled: domainUser?.enabled ?? false,
    isAuthenticated: authStatus === 'authenticated',
    isLoading: authStatus === 'loading' || domainUserStatus === 'loading',
    isError: meQuery.isError,
    error: meQuery.error,
    refetch: meQuery.refetch,
  }
}
